import { differenceInHours } from 'date-fns';
import { usePathname } from 'next/navigation';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import type { Tournament } from '@/api/cms/tournaments';
import { IconComponent } from '@/components/IconComponent';
import { NativeSlider } from '@/components/NativeSlider';
import { STORAGE, useGetCommonStore, useLocalStorageStore } from '@/store';
import { ScTournamentsWidget } from './styled';
import { TournamentInfoSlide } from './TournamentInfoSlide';
import { TournamentModal } from './TournamentModal';

interface TournamentsWidgetProps {
  gameTournaments: Tournament[];
}

export const TournamentsWidget: FC<TournamentsWidgetProps> = ({
  gameTournaments,
}) => {
  const useCommonStore = useGetCommonStore();

  const currentPage = usePathname();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const tournamentBannerLastClose = useLocalStorageStore
    .getState()
    .get(STORAGE.TOURNAMENT_BANNER_CLOSED);

  const [isTournamentBannerCollapsed, setTournamentBannerCollapsed] =
    useState<boolean>(false);

  const [selectedTournament, setSelectedTournament] =
    useState<Tournament | null>(null);

  useEffect(() => {
    if (selectedTournament) {
      return () => {
        setSelectedTournament(null);
      };
    }
  }, [selectedTournament, currentPage]);

  useEffect(() => {
    if (!tournamentBannerLastClose && isTournamentBannerCollapsed) {
      const currentDate = new Date();
      useLocalStorageStore
        .getState()
        .push(STORAGE.TOURNAMENT_BANNER_CLOSED, currentDate.getTime());
    }
  }, [tournamentBannerLastClose, isTournamentBannerCollapsed]);

  useEffect(() => {
    if (tournamentBannerLastClose && isMobileDevice) {
      const currentDate = new Date();
      let lastCloseDate: Date | null = null;

      if (
        typeof tournamentBannerLastClose === 'string' ||
        typeof tournamentBannerLastClose === 'number'
      ) {
        lastCloseDate = new Date(tournamentBannerLastClose);
      }

      if (lastCloseDate && !isNaN(lastCloseDate.getTime())) {
        const timeDiff = differenceInHours(currentDate, lastCloseDate);

        if (timeDiff <= 1) {
          setTournamentBannerCollapsed(true);
        } else {
          useLocalStorageStore
            .getState()
            .remove(STORAGE.TOURNAMENT_BANNER_CLOSED);
        }
      }
    }
  }, [isMobileDevice, tournamentBannerLastClose]);

  const autoplayOptions = useMemo(
    () =>
      gameTournaments.length > 1
        ? {
            autoplay: {
              delay: isTournamentBannerCollapsed ? 5000 : 15_000,
              pauseOnMouseEnter: true,
            },
          }
        : {},
    [gameTournaments.length, isTournamentBannerCollapsed]
  );

  const getTournamentSlide = useCallback(
    (tournament: Tournament) =>
      isTournamentBannerCollapsed ? (
        <div
          className="collapsed-banner__content"
          onClick={() => setTournamentBannerCollapsed(false)}
        >
          <p>{tournament.name}</p>
          <IconComponent name="whiteArrowRight" size="20px" />
        </div>
      ) : (
        <TournamentInfoSlide
          {...tournament}
          onClick={() => setSelectedTournament(tournament)}
          setTournamentBannerCollapsed={setTournamentBannerCollapsed}
        />
      ),
    [isTournamentBannerCollapsed]
  );

  return gameTournaments.length ? (
    <ScTournamentsWidget isMobile={isMobileDevice}>
      <NativeSlider<Tournament>
        key={isTournamentBannerCollapsed ? 'collapsed-banners' : 'tournaments'}
        lazyLoadingOf={2}
        loop
        slide={getTournamentSlide}
        slides={gameTournaments}
        {...autoplayOptions}
      />
      {selectedTournament && (
        <TournamentModal
          onClose={() => setSelectedTournament(null)}
          {...selectedTournament}
        />
      )}
    </ScTournamentsWidget>
  ) : null;
};
